// Spreadsheet formula engine — MIRROR of backend skillstudio.py (eval_ref/eval_formula).
// Supports: absolute refs ($A$1), arithmetic (+ - * / parens), comparisons, and
// SUM AVERAGE COUNT COUNTA MAX MIN IF COUNTIF SUMIF ROUND TODAY.
export const colToIdx = (col) => { let i = 0; for (const ch of col) i = i * 26 + (ch.toUpperCase().charCodeAt(0) - 64); return i - 1; };
export const idxToCol = (i) => { let s = ""; i += 1; while (i > 0) { const r = (i - 1) % 26; s = String.fromCharCode(65 + r) + s; i = Math.floor((i - 1) / 26); } return s; };
export const stripAbs = (s) => (s || "").replace(/\$/g, "");
export const parseRef = (ref) => { const m = /^([A-Za-z]+)(\d+)$/.exec(stripAbs(ref).trim()); if (!m) return null; return [colToIdx(m[1]), parseInt(m[2], 10) - 1]; };

export const expandRange = (rng) => {
  rng = stripAbs(rng).trim();
  if (rng.includes(":")) {
    const [a, b] = rng.split(":");
    const pa = parseRef(a), pb = parseRef(b);
    if (!pa || !pb) return [];
    const refs = [];
    for (let r = Math.min(pa[1], pb[1]); r <= Math.max(pa[1], pb[1]); r++)
      for (let c = Math.min(pa[0], pb[0]); c <= Math.max(pa[0], pb[0]); c++)
        refs.push(idxToCol(c) + (r + 1));
    return refs;
  }
  return [rng];
};

// grid of refs for a range (rows of columns), for chart rendering
export const rangeGrid = (rng) => {
  if (!rng || !rng.includes(":")) return null;
  const [a, b] = stripAbs(rng).split(":");
  const pa = parseRef(a), pb = parseRef(b);
  if (!pa || !pb) return null;
  const grid = [];
  for (let r = Math.min(pa[1], pb[1]); r <= Math.max(pa[1], pb[1]); r++) {
    const row = [];
    for (let c = Math.min(pa[0], pb[0]); c <= Math.max(pa[0], pb[0]); c++) row.push(idxToCol(c) + (r + 1));
    grid.push(row);
  }
  return grid;
};

const num = (v) => { if (v === "" || v === null || v === undefined || typeof v === "boolean") return null; const n = Number(v); return Number.isNaN(n) ? null : n; };
const ERR = "#ERR";
const isErr = (v) => v === ERR;

export function evalRef(cells, ref, seen) {
  seen = seen || new Set();
  ref = stripAbs(ref);
  if (seen.has(ref)) return null;
  const raw = String(cells[ref] == null ? "" : cells[ref]).trim();
  if (raw === "") return null;
  if (raw.startsWith("=")) { const s = new Set(seen); s.add(ref); return evalFormula(cells, raw, s); }
  const n = num(raw);
  return n === null ? raw : n;
}

// ---------------- expression parser ----------------
const AGG = { SUM: 1, AVERAGE: 1, COUNT: 1, COUNTA: 1, MAX: 1, MIN: 1 };

function criteriaMatch(value, crit) {
  if (crit === null || crit === undefined) return false;
  let op = "=", target = crit;
  if (typeof crit === "string") {
    const m = /^\s*(>=|<=|<>|>|<|=)?\s*(.*)$/.exec(crit);
    op = m[1] || "="; target = m[2];
  }
  const tn = num(target), vn = num(value);
  if (tn !== null && vn !== null) {
    if (op === "=") return vn === tn;
    if (op === "<>") return vn !== tn;
    if (op === ">") return vn > tn;
    if (op === "<") return vn < tn;
    if (op === ">=") return vn >= tn;
    if (op === "<=") return vn <= tn;
  }
  const vs = String(value == null ? "" : value).trim().toLowerCase();
  const ts = String(target == null ? "" : target).trim().toLowerCase();
  if (op === "<>") return vs !== ts;
  return vs === ts;
}

function refsOf(arg) { return arg && arg.__refs ? arg.__refs : null; }

function numsIn(cells, arg, seen) {
  const refs = refsOf(arg);
  const out = [];
  if (refs) { refs.forEach((r) => { const n = num(evalRef(cells, r, seen)); if (n !== null) out.push(n); }); return out; }
  const n = num(arg);
  if (n !== null) out.push(n);
  return out;
}

function valuesIn(cells, arg, seen) {
  const refs = refsOf(arg);
  if (refs) return refs.map((r) => evalRef(cells, r, seen));
  return [arg];
}

function callFn(cells, fn, args, seen) {
  if (fn === "TODAY") return new Date().toISOString().slice(0, 10);
  if (AGG[fn]) {
    if (fn === "COUNTA") {
      let n = 0;
      args.forEach((a) => valuesIn(cells, a, seen).forEach((v) => { if (v !== null && v !== undefined && String(v).trim() !== "") n += 1; }));
      return n;
    }
    const vals = [];
    args.forEach((a) => vals.push(...numsIn(cells, a, seen)));
    if (fn === "SUM") return vals.reduce((a, b) => a + b, 0);
    if (fn === "COUNT") return vals.length;
    if (fn === "AVERAGE") return vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10000) / 10000 : 0;
    if (fn === "MAX") return vals.length ? Math.max(...vals) : 0;
    return vals.length ? Math.min(...vals) : 0;
  }
  if (fn === "IF") {
    if (args.length < 2) return ERR;
    const c = args[0];
    const cv = refsOf(c) ? evalRef(cells, refsOf(c)[0], seen) : c;
    const truthy = typeof cv === "boolean" ? cv : num(cv) !== null ? num(cv) !== 0 : String(cv || "").trim() !== "";
    const pick = truthy ? args[1] : (args.length > 2 ? args[2] : "");
    return refsOf(pick) ? evalRef(cells, refsOf(pick)[0], seen) : pick;
  }
  if (fn === "COUNTIF" || fn === "SUMIF") {
    const refs = refsOf(args[0]);
    if (!refs || args.length < 2) return ERR;
    const crit = refsOf(args[1]) ? evalRef(cells, refsOf(args[1])[0], seen) : args[1];
    const sumRefs = args.length > 2 ? refsOf(args[2]) : null;
    let count = 0, total = 0;
    refs.forEach((r, i) => {
      if (!criteriaMatch(evalRef(cells, r, seen), crit)) return;
      count += 1;
      const target = sumRefs && i < sumRefs.length ? sumRefs[i] : r;
      const n = num(evalRef(cells, target, seen));
      if (n !== null) total += n;
    });
    return fn === "COUNTIF" ? count : total;
  }
  if (fn === "ROUND") {
    const v = numsIn(cells, args[0], seen)[0];
    if (v === undefined) return ERR;
    const d = args.length > 1 ? (numsIn(cells, args[1], seen)[0] || 0) : 0;
    const f = Math.pow(10, d);
    return Math.round(v * f) / f;
  }
  return ERR;
}

function parse(cells, src, seen) {
  let i = 0;
  const ws = () => { while (i < src.length && /\s/.test(src[i])) i += 1; };
  const scalar = (v) => (refsOf(v) ? evalRef(cells, refsOf(v)[0], seen) : v);

  function primary() {
    ws();
    if (i >= src.length) return ERR;
    const ch = src[i];
    if (ch === "(") { i += 1; const v = expr(); ws(); if (src[i] === ")") i += 1; return v; }
    if (ch === "-") { i += 1; const v = num(scalar(primary())); return v === null ? ERR : -v; }
    if (ch === '"') {
      i += 1; let s = "";
      while (i < src.length && src[i] !== '"') { s += src[i]; i += 1; }
      i += 1;
      return s;
    }
    const nm = /^\d+(\.\d+)?/.exec(src.slice(i));
    if (nm) { i += nm[0].length; return Number(nm[0]); }
    const idm = /^\$?[A-Za-z]+\$?\d*/.exec(src.slice(i));
    if (!idm) return ERR;
    const tok = idm[0];
    i += tok.length;
    ws();
    if (src[i] === "(") {           // function call
      i += 1;
      const args = [];
      ws();
      if (src[i] !== ")") {
        for (;;) { args.push(expr()); ws(); if (src[i] === ",") { i += 1; continue; } break; }
      }
      ws();
      if (src[i] === ")") i += 1;
      return callFn(cells, stripAbs(tok).toUpperCase(), args, seen);
    }
    if (!/\d/.test(tok)) return ERR; // a bare name that isn't a call or a ref
    if (src[i] === ":") {            // range
      i += 1;
      ws();
      const m2 = /^\$?[A-Za-z]+\$?\d+/.exec(src.slice(i));
      if (!m2) return ERR;
      i += m2[0].length;
      return { __refs: expandRange(`${tok}:${m2[0]}`) };
    }
    return { __refs: [stripAbs(tok)] };
  }

  function mul() {
    let left = primary();
    for (;;) {
      ws();
      const op = src[i];
      if (op !== "*" && op !== "/") return left;
      i += 1;
      const a = num(scalar(left)), b = num(scalar(mul_one()));
      if (a === null || b === null) return ERR;
      left = op === "*" ? a * b : (b === 0 ? ERR : a / b);
      if (isErr(left)) return ERR;
    }
  }
  function mul_one() { return primary(); }

  function add() {
    let left = mul();
    for (;;) {
      ws();
      const op = src[i];
      if (op !== "+" && op !== "-") return left;
      i += 1;
      const a = num(scalar(left)), b = num(scalar(mul()));
      if (a === null || b === null) return ERR;
      left = op === "+" ? a + b : a - b;
    }
  }

  function expr() {
    const left = add();
    ws();
    const two = src.slice(i, i + 2);
    const op = [">=", "<=", "<>"].includes(two) ? two : ([">", "<", "="].includes(src[i]) ? src[i] : null);
    if (!op) return left;
    i += op.length;
    const right = add();
    const lv = scalar(left), rv = scalar(right);
    const ln = num(lv), rn = num(rv);
    if (ln !== null && rn !== null) {
      return op === "=" ? ln === rn : op === "<>" ? ln !== rn : op === ">" ? ln > rn : op === "<" ? ln < rn : op === ">=" ? ln >= rn : ln <= rn;
    }
    const ls = String(lv == null ? "" : lv).trim().toLowerCase(), rs = String(rv == null ? "" : rv).trim().toLowerCase();
    return op === "<>" ? ls !== rs : ls === rs;
  }

  const out = expr();
  return refsOf(out) ? evalRef(cells, refsOf(out)[0], seen) : out;
}

export function evalFormula(cells, raw, seen) {
  try {
    const v = parse(cells, String(raw).replace(/^=/, ""), seen || new Set());
    if (v === undefined) return ERR;
    if (typeof v === "number") return Number.isFinite(v) ? Math.round(v * 10000) / 10000 : ERR;
    return v;
  } catch (e) { return ERR; }
}

// ---------------- number formats ----------------
export const NUM_FORMATS = [
  { id: "plain", name: "Plain" },
  { id: "currency", name: "Currency" },
  { id: "percent", name: "Percent" },
  { id: "decimal", name: "2 decimals" },
  { id: "date", name: "Date" },
];

const commas = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export function formatValue(v, fmt) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  if (typeof v !== "number") return String(v);
  if (fmt === "currency") { const s = Math.abs(v).toFixed(2); return `${v < 0 ? "-" : ""}$${commas(s.split(".")[0])}.${s.split(".")[1]}`; }
  if (fmt === "percent") return `${Math.round(v * 100 * 100) / 100}%`;
  if (fmt === "decimal") return v.toFixed(2);
  if (fmt === "date") { const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10); }
  return String(Math.round(v * 10000) / 10000);
}

export function cellDisplay(cells, ref, fmt) {
  const v = evalRef(cells, ref);
  return formatValue(v, fmt);
}

// Fill a formula down a column: "=B{r}*$B$1" with {r} replaced by each row number.
export const fillPattern = (pattern, row) => String(pattern).replace(/\{r\}/g, String(row));

export function shiftFormulaRows(formula, delta) {
  // Relative rows move, absolute ($2) stay — what the fill handle does in both suites.
  return String(formula).replace(/(\$?)([A-Za-z]+)(\$?)(\d+)/g, (m, c$, col, r$, row) =>
    `${c$}${col}${r$}${r$ ? row : String(Math.max(1, parseInt(row, 10) + delta))}`);
}
